import styles from './styles.module.css';
import { Navigate } from '@/util/react-router-dom';
import { useRegister } from '@/api/register';
import {
  RegisterSuccessResponse,
  RegisterErrorType,
  RegisterErrorResponse,
} from '@/api/api.schemas';
import { useTranslation } from '@/contexts/i18n-context';
import clsx from 'clsx';
import { Section } from '@/components/containers/section';
import { useBranding } from '@/outlets/branding-outlet';
import { RegisterLayout } from '@/components/auth/register-layout';
import { RegisterForm, RegisterFullFormData } from '@/components/auth/register-form';

export function RegisterPage() {
  const { t } = useTranslation();

  function getErrorMessage(error?: RegisterErrorResponse | null) {
    if (!error) {
      return;
    }

    switch (error.type) {
      case RegisterErrorType.UserAlreadyExists:
        return t('register.errors.userAlreadyExists');
      case RegisterErrorType.ValidationFailed:
        return t('register.errors.validationFailed');
      case RegisterErrorType.InvalidUsername:
        return t('register.errors.invalidUsername');
      default:
        return t('common.errors.unknown');
    }
  }

  const { mutate: register, isPending, error } = useRegister();
  const { createAccountEnabled, networkId } = useBranding();

  const onSubmit: (
    data: RegisterFullFormData,
    onSuccess: (result: RegisterSuccessResponse) => void,
  ) => void = (data, onSuccess) => {
    register({ data: { ...data, networkId } }, { onSuccess });
  };

  if (!createAccountEnabled) {
    return <Navigate to="/login" replace />;
  }

  return (
    <RegisterLayout>
      <Section isFormSection={false}>
        <h2 className="h4">{t('register.formTitle')}</h2>
        <p className={clsx('body-2', styles.helperText)}>{t('register.helperText')}</p>
        <RegisterForm
          type="full"
          onSubmit={onSubmit}
          error={getErrorMessage(error)}
          loading={isPending}
        />
      </Section>
    </RegisterLayout>
  );
}
